// src/components/elements/Content.js
import React, { useState, useEffect, useRef } from 'react';
import { useMemo } from 'react';
import Background from './Background';
// Importiere den usePageTheme Hook
import { usePageTheme } from './context/PageThemeContext';
import './Content.css';

const STRAPI_BASE_URL = process.env.REACT_APP_STRAPI_BASE_URL;

const Content = ({
  Description,
  ContentAlignment,
  Media, // Media.data ist jetzt ein ARRAY von Medienobjekten
  BackgroundType,
  BackgroundColorHexCode,
  BackgroundMedia,
  TextColorHexCode,
  Frame,
  FrameColorHexCode,
  ContentLink,
  BodyStyle,
  TextAlignment
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const slideshowRef = useRef(null);

   // Den Context hier konsumieren
  const { overallPageBackgroundColor } = usePageTheme();

  // Der Breakpoint für Desktop bleibt md (768px), da das Karussell-Layout dort anders ist
  const [isScreenMdUp, setIsScreenMdUp] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsScreenMdUp(window.innerWidth >= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const imagesForSlideshow = (Media && Array.isArray(Media.data)) ? Media.data : [];
  const isMediaSlideshow = imagesForSlideshow.length > 1;

  const invisibleLineCount = useMemo(() => {
  let count = 0;

  Description?.forEach(block => {
    if (
      block.type === 'paragraph' &&
      block.children?.length === 1 &&
      typeof block.children[0].text === 'string' &&
      block.children[0].text.trim().startsWith('---')
    ) {
      count++;
    }
    else if(
      block.type === 'paragraph' &&
      block.children?.length === 1 &&
      typeof block.children[0].text === 'string' &&
      !block.children[0].text.trim().startsWith('---')
    ){
      count = 0;
    }
  });

  return count;
}, [Description]);

  let imagesPerView = 3;

  const goToNextSlide = () => {
    if(isScreenMdUp){
    setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % (imagesForSlideshow.length - (imagesPerView - 1)));
    }
    else{
      setCurrentSlideIndex((prevIndex) => (prevIndex + 1) % (imagesForSlideshow.length));
    }
  };

  const goToPrevSlide = () => {
    if(isScreenMdUp){
    setCurrentSlideIndex((prevIndex) => (prevIndex - 1 + imagesForSlideshow.length) % (imagesForSlideshow.length - (imagesPerView - 1)));
    }
    else{
      setCurrentSlideIndex((prevIndex) => (prevIndex - 1 + imagesForSlideshow.length) % (imagesForSlideshow.length));
    }
  };

  // --- Touch-Event-Logik für Mobile Swipe (jetzt immer für Diashow aktiv) ---
  useEffect(() => {
    // Diese Logik ist relevant, wenn es eine Diashow gibt, UNABHÄNGIG von der Bildschirmgröße
    if (!isMediaSlideshow) return;

    const element = slideshowRef.current;
    if (!element) return;

    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      touchEndX = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
      // Swipe-Schwellenwert bleibt gleich
      if (touchStartX - touchEndX > 50) { // Swipe nach links (nächster Slide)
        goToNextSlide();
      } else if (touchEndX - touchStartX > 50) { // Swipe nach rechts (vorheriger Slide)
        goToPrevSlide();
      }
      touchStartX = 0;
      touchEndX = 0;
    };

    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchmove', handleTouchMove);
    element.addEventListener('touchend', handleTouchEnd);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMediaSlideshow, imagesForSlideshow.length]); // isScreenMdUp hier entfernt

  const isMediaTypeVideo = (mediaAttributes) => {
    return mediaAttributes && mediaAttributes.mime && mediaAttributes.mime.startsWith('video/');
  };

  const isLightColor = (hex) => {
    if (!hex || hex.length < 7) return true;
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 > 128;
  };

  const splitDescriptionIntoColumns = (descriptionBlocks) => {
    const columns = [[]];
    let currentColumnIndex = 0;
    const MAX_COLUMNS = 3;

    descriptionBlocks.forEach(block => {
      const isNextColumnMarker = block.type === 'paragraph' &&
                                  block.children &&
                                  block.children.length === 1 &&
                                  block.children[0].type === 'text' &&
                                  block.children[0].text.trim().toLowerCase() === 'next-column';

      if (isNextColumnMarker) {
        if (currentColumnIndex < MAX_COLUMNS - 1) {
          currentColumnIndex++;
          columns.push([]);
        }
      } else {
        columns[currentColumnIndex].push(block);
      }
    });
    return columns;
  };

  const renderRichTextBlocks = (blocks, textColorStyle) => {
    if (!blocks || blocks.length === 0) return null;

    return blocks.map((block, blockIndex) => {
        const renderTextChildren = (children) => {
            if (!children) return null;

            return children.map((child, childIndex) => {
                let currentText = child.text;
                let elementStyle = { ...textColorStyle };
                let renderedContent;

                if (typeof currentText === 'string') {
                    const match = currentText.match(/^([-—–]+)\s*(.*)$/);

                    if (match) {
                        const dashes = match[1];
                        const remainingText = match[2];

                        renderedContent = (
                            <>
                                <span style={{ ...elementStyle, color: 'transparent' }} key={`dash-${childIndex}`}>
                                    {dashes}
                                </span>
                                {remainingText && <span key={`text-${childIndex}`}>{remainingText}</span>}
                            </>
                        );
                    }
                }

                if (!renderedContent) {
                    let baseContent = currentText;

                    if (child.bold) baseContent = <strong key={`b-${childIndex}`}>{baseContent}</strong>;
                    if (child.italic) baseContent = <em key={`i-${childIndex}`}>{baseContent}</em>;
                    if (child.underline) baseContent = <u key={`u-${childIndex}`}>{baseContent}</u>;
                    if (child.strikethrough) baseContent = <del key={`d-${childIndex}`}>{baseContent}</del>;

                    if (child.type === 'link' && child.url) {
                         const buttonBaseBgColor = overallPageBackgroundColor || '#007bff'; // <-- Angepasst!
                        const buttonTextColor = isLightColor(buttonBaseBgColor) ? '#000000' : '#ffffff';

                        return (
                            <a
                                key={`link-${childIndex}`}
                                href={child.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn"
                                style={{
                                  backgroundColor: buttonBaseBgColor,
                                  color: buttonTextColor
                                }}
                                onMouseOver={(e) => e.currentTarget.style.filter = 'var(--button-hover-filter)'}
                                onMouseOut={(e) => e.currentTarget.style.filter = 'brightness(0.85)'}
                            >
                                {renderTextChildren(child.children)}
                            </a>
                        );
                    }

                  if (BackgroundType === 'Transparent') {
                    if (React.isValidElement(baseContent)) {
                        renderedContent = React.cloneElement(baseContent, {
                            style: { ...elementStyle, ...baseContent.props.style }
                        });
                    } else {
                        renderedContent = <span key={`span-${childIndex}`} style={{ ...elementStyle}}>{baseContent}</span>;
                    }
                  }
                  else{
                    if (React.isValidElement(baseContent)) {
                        renderedContent = React.cloneElement(baseContent, {
                            style: {  backgroundColor: BackgroundColorHexCode, ...elementStyle, ...baseContent.props.style }
                        });
                    } else {
                        renderedContent = <span key={`span-${childIndex}`} style={{backgroundColor: BackgroundColorHexCode, ...elementStyle}}>{baseContent}</span>;
                    }
                  }
                }

                return renderedContent;
            });
        };

        switch (block.type) {
            case 'paragraph':
                return <p key={blockIndex} style={textColorStyle}>{renderTextChildren(block.children)}</p>;
            case 'heading':
                const HeadingTag = `h${block.level}`;
                return <HeadingTag key={blockIndex} style={textColorStyle}>{renderTextChildren(block.children)}</HeadingTag>;
            case 'list':
                const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
                return (
                    <ListTag key={blockIndex} style={textColorStyle}>
                        {block.children.map((listItem, itemIndex) => (
                            <li key={`${blockIndex}-${itemIndex}`}>
                                {renderTextChildren(listItem.children)}
                            </li>
                        ))}
                    </ListTag>
                );
            default:
                console.warn(`Unbekannter Rich Text Block Typ: ${block.type}`, block);
                return null;
        }
    });
};

  const frameStyle = {};
  if (FrameColorHexCode && Frame === 'yes') {
    frameStyle.border = `2px solid ${FrameColorHexCode}`;
    frameStyle.padding = '8px';
  }

  const hasMedia = imagesForSlideshow.length > 0;

  const hasVisualBackground = BackgroundType === 'Media' || (BackgroundType === 'Color');

  const contentTextColorStyle = {
    color: TextColorHexCode || '#000000',
    textAlign:
      TextAlignment === 'right'
        ? 'right'
        : TextAlignment === 'center'
          ? 'center'
          : 'left',
  };

  let contentWrapperAdditionalStyle = {};

  if (BodyStyle === 'style3' && isScreenMdUp) {
    if (!hasVisualBackground) {
      contentWrapperAdditionalStyle = {
        maxWidth: 'calc(100% / 3 * 2)',
        marginLeft: 'auto',
        marginRight: 'auto',
      };
    } else {
      contentWrapperAdditionalStyle = {};
    }
  }

  const textColumns = Description ? splitDescriptionIntoColumns(Description) : [[]];
  const totalColumns = textColumns.length;

  let textOrderClass = '';
  let mediaOrderClass = '';
  let textColumnSize = '';
  let mediaColumnSize = '';

  switch (ContentAlignment) {
    case 'right':
      textOrderClass = 'order-2';
      mediaOrderClass = 'order-1';
      textColumnSize = 'col-md-8';
      mediaColumnSize = 'col-md-4';

      if (!hasMedia) {
        textColumnSize = 'col-12';
        mediaColumnSize = 'd-none';
      }
      break;
    case 'center':
      textOrderClass = 'order-2';
      mediaOrderClass = 'order-1';
      textColumnSize = 'col-12';
      mediaColumnSize = hasMedia ? 'col-12' : 'd-none';
      break;
    case 'left':
    default:
      textOrderClass = 'order-1';
      mediaOrderClass = 'order-2';
      textColumnSize = 'col-md-8';
      mediaColumnSize = 'col-md-4';

      if (!hasMedia) {
        textColumnSize = 'col-12';
        mediaColumnSize = 'd-none';
      }
      break;
  }

  const ContentWrapper = ContentLink ? 'a' : 'div';
  const wrapperProps = ContentLink ? {
    href: ContentLink,
    target: "_blank",
    rel: "noopener noreferrer",
    style: {
      display: 'block',
      textDecoration: 'none',
      color: 'inherit',
      ...frameStyle,
      ...contentWrapperAdditionalStyle
    }
  } : {
    style: {
        ...frameStyle,
        ...contentWrapperAdditionalStyle
    }
  };

  return (
    <ContentWrapper
      className={`content-element py-0 px-0 rounded shadow-add h-100 ${ContentLink ? 'clickable-content' : ''}`}
      {...wrapperProps}
    >
      <Background
        BackgroundType={BackgroundType}
        BackgroundColorHexCode={BackgroundColorHexCode}
        Media={BackgroundMedia}
      />

      {/* Drei leere Textzeilen (unsichtbar, aber mit Höhe) */}
        {[...Array(invisibleLineCount)].map((_, i) => (
          <div key={`spacer-${i}`} style={{ opacity: 0 }}>
            ---
          </div>
        ))}

      <div className={`row gx-0 position-relative align-items-center`} style={{ zIndex: 1 }}>
        <div className={`${textOrderClass} ${hasMedia ? textColumnSize : 'col-12'}`}>
          <div className="row gx-0 px-0 h-100 align-items-stretch">
            {textColumns.map((columnBlocks, colIndex) => (
              <div
                key={`col-${colIndex}`}
                className={`col-12 col-md-${12 / Math.min(totalColumns, 3)}`}
                //style={{ paddingLeft: '-50px', background: 'lightblue' }}
              >
                {renderRichTextBlocks(columnBlocks, contentTextColorStyle)}
              </div>
            ))}
          </div>
        </div>
        {hasMedia && (
          <div
            className={`${mediaOrderClass} ${mediaColumnSize} text-center position-relative overflow-hidden`} // overflow-hidden ist wichtig!
            ref={slideshowRef}
          >
            {isMediaSlideshow ? (
              // Der Slideshow-Container, der alle Bilder enthält und verschoben wird
              //###########################
              isScreenMdUp?(
              <div
                className="d-flex"
                style={{
                  width: `100%`, // Gesamtbreite für alle Bilder
                  transform: `translateX(-${currentSlideIndex * 33}%)`, // Verschiebt den Container
                  transition: 'transform 0.5s ease-in-out' // Die Animation
                }}
              >
                {imagesForSlideshow.map((image, index) => (
                  // Jedes Bild nimmt 100% der Breite des "Sichtfensters" ein
               
                    <img
                      src={`${STRAPI_BASE_URL}${image.attributes.url}`}
                      alt={image.attributes.alternativeText || `Slideshow Image ${index + 1}`}
                      className="img-fluid"
                      style={{ width: '33%'}}
                    />
                
                ))}
              </div>
              ):( <div
                className="d-flex"
                style={{
                  width: `100%`, // Gesamtbreite für alle Bilder
                  transform: `translateX(-${currentSlideIndex * 100}%)`, // Verschiebt den Container
                  transition: 'transform 0.5s ease-in-out' // Die Animation
                }}
              >
                {imagesForSlideshow.map((image, index) => (
                  // Jedes Bild nimmt 100% der Breite des "Sichtfensters" ein
               
                    <img
                      src={`${STRAPI_BASE_URL}${image.attributes.url}`}
                      alt={image.attributes.alternativeText || `Slideshow Image ${index + 1}`}
                      className="img-fluid"
                      style={{ maxHeight: '400px', width: '100%', objectFit: 'cover' }}
                    />
                
                ))}
                </div>
                )
              
            
              //###################################
            ) : (
              // Fallback für EINZELNES Bild oder Video
              imagesForSlideshow.length === 1 && imagesForSlideshow[0].attributes && (
                isMediaTypeVideo(imagesForSlideshow[0].attributes) ? (
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    src={`${STRAPI_BASE_URL}${imagesForSlideshow[0].attributes.url}`}
                    type={imagesForSlideshow[0].attributes.mime}
                    alt={imagesForSlideshow[0].attributes.alternativeText || 'Content Video'}
                    className="img-fluid"
                    style={{
                      maxHeight: '400px',
                      width: 'auto',
                      height: 'auto',
                    }}
                  >
                    Dein Browser unterstützt dieses Video nicht.
                  </video>
                ) : (
                  <img
                    src={`${STRAPI_BASE_URL}${imagesForSlideshow[0].attributes.url}`}
                    alt={imagesForSlideshow[0].attributes.alternativeText || 'Content Image'}
                    className="img-fluid"
                    style={{
                      maxHeight: '400px',
                      width: 'auto',
                      height: 'auto',
                    }}
                  />
                )
              )
            )}

            {isMediaSlideshow && ( // Navigationsbuttons nur anzeigen, wenn es eine Diashow gibt
            isScreenMdUp?(
                <>
                    <button
                        className="btn btn-dark position-absolute top-50 start-0 translate-middle-y ms-2"
                        onClick={goToPrevSlide}
                    >
                        &lt;
                    </button>
                    <button
                        className="btn btn-dark position-absolute top-50 end-0 translate-middle-y me-2"
                        onClick={goToNextSlide}
                    >
                        &gt;
                    </button>
                </>
            ):(
              <>
                    <button
                        className="btn btn-dark position-absolute top-50 start-0 translate-middle-y ms-2"
                    >
                        &lt;
                    </button>
                    <button
                        className="btn btn-dark position-absolute top-50 end-0 translate-middle-y me-2"
                    >
                        &gt;
                    </button>
                </>
            )
            )}
          </div>
        )}
      </div>
    </ContentWrapper>
  );
};

export default Content;